import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, ArrowLeft, Check, Hammer, Loader2, Plus, X, Clock, ImagePlus } from 'lucide-react';
import { serviceIconSvg } from './serviceIcons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback presets (used only if the API call fails). Keep in sync with
// TRADE_PRESETS in backend/server.py.
const FALLBACK_PRESETS = {
  electrician: { label: 'Electrician', services: [] },
  plumber: { label: 'Plumber', services: [] },
  hvac: { label: 'Air conditioning / HVAC', services: [] },
  cleaner: { label: 'Cleaner', services: [] },
  landscaper: { label: 'Landscaper', services: [] },
  handyman: { label: 'Handyman', services: [] },
  gasfitter: { label: 'Gas fitter', services: [] },
  builder: { label: 'Builder', services: [] },
  detailer: { label: 'Car detailer', services: [] },
  other: { label: 'Other', services: [] },
};
const FALLBACK_LICENCE_TRADES = ['electrician', 'plumber', 'hvac', 'handyman', 'gasfitter', 'builder'];
const FALLBACK_LICENCE_LABELS = {
  electrician: 'Electrical licence number',
  plumber: 'Plumbing licence number',
  hvac: 'Refrigeration/AC licence number',
  gasfitter: 'Gas fitting licence number',
  builder: "Builder's licence number",
  handyman: 'Licence number',
};
const DEFAULT_HOURS = 'Mon-Fri 7:00am-5:00pm';

/**
 * GuidedIntake — 2-step, mobile-first wizard that replaces the blank "Ask
 * YouBuild to create a website" prompt.
 *
 * Props:
 *   initial       – optional pre-fill answers (from /projects/{id}/intake).
 *   submitLabel   – button label on the final step (default "Build my website").
 *   busy          – disables the submit button while parent is saving.
 *   onSubmit      – async (intake) => void. Parent is responsible for
 *                   creating/updating the project and posting to
 *                   /projects/{id}/generate-guided.
 *   onAdvanced    – optional callback that switches to the free-text prompt.
 */
const GuidedIntake = ({ initial, submitLabel = 'Build my website', busy = false, onSubmit, onAdvanced }) => {
  const [presets, setPresets] = useState(FALLBACK_PRESETS);
  const [licenceTrades, setLicenceTrades] = useState(FALLBACK_LICENCE_TRADES);
  const [licenceLabels, setLicenceLabels] = useState(FALLBACK_LICENCE_LABELS);
  const [step, setStep] = useState(1);

  const [trade, setTrade] = useState(initial?.trade || '');
  const [tradeOther, setTradeOther] = useState(initial?.trade_other || '');
  const [businessName, setBusinessName] = useState(initial?.business_name || '');
  const [town, setTown] = useState(initial?.town || '');
  const [areas, setAreas] = useState(Array.isArray(initial?.areas) ? initial.areas : []);
  const [areaDraft, setAreaDraft] = useState('');
  const [services, setServices] = useState(Array.isArray(initial?.services) ? initial.services : []);
  const [serviceDraft, setServiceDraft] = useState('');
  const [phone, setPhone] = useState(initial?.phone || '');

  const [hours, setHours] = useState(initial?.hours != null ? initial.hours : DEFAULT_HOURS);
  const [licenceNumber, setLicenceNumber] = useState(initial?.licence_number || '');
  const [abn, setAbn] = useState(initial?.abn || '');
  const [licensedInsured, setLicensedInsured] = useState(
    initial?.licensed_insured === true ? 'yes' :
    initial?.licensed_insured === false ? 'no' : ''
  );
  // Neutral operational default — never pre-checked. Owner opts in.
  const [emergencyCallouts, setEmergencyCallouts] = useState(!!initial?.emergency_callouts);

  const [errors, setErrors] = useState({});

  // Fetch trade presets once. Cheap public endpoint.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/intake/presets`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (data?.presets) setPresets(data.presets);
        if (Array.isArray(data?.licence_trades)) setLicenceTrades(data.licence_trades);
        if (data?.licence_labels && typeof data.licence_labels === 'object') {
          setLicenceLabels(data.licence_labels);
        }
      } catch (_) { /* keep fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // When a trade is chosen (and no explicit services were passed in via
  // `initial`), show the full preset list unchecked so the user can tick
  // what they offer. Never overwrite an already-picked list on re-selection.
  const [servicesTouched, setServicesTouched] = useState(
    Array.isArray(initial?.services) && initial.services.length > 0
  );
  const availableServices = useMemo(() => {
    const preset = presets[trade];
    const list = preset?.services || [];
    // Merge preset + any custom services the user has added so the UI
    // shows both in one column with a consistent tick UX.
    const merged = [...list];
    for (const s of services) {
      if (!merged.includes(s)) merged.push(s);
    }
    return merged;
  }, [presets, trade, services]);

  const showLicenceField = trade && licenceTrades.includes(trade);
  const licenceFieldLabel = licenceLabels[trade] || 'Licence number';

  const toggleService = (name) => {
    setServicesTouched(true);
    setServices((prev) => (
      prev.includes(name) ? prev.filter((s) => s !== name) : [...prev, name]
    ));
  };
  const selectAllServices = () => {
    setServicesTouched(true);
    setServices(availableServices.slice());
  };
  const clearAllServices = () => {
    setServicesTouched(true);
    setServices([]);
  };
  const addCustomService = () => {
    const v = serviceDraft.trim();
    if (!v) return;
    setServicesTouched(true);
    setServices((prev) => (prev.includes(v) ? prev : [...prev, v]));
    setServiceDraft('');
  };

  const addArea = () => {
    const v = areaDraft.trim();
    if (!v) return;
    setAreas((prev) => (prev.includes(v) ? prev : [...prev, v]));
    setAreaDraft('');
  };
  const removeArea = (v) => setAreas((prev) => prev.filter((a) => a !== v));

  const validateStep1 = () => {
    const e = {};
    if (!trade) e.trade = 'Pick your trade.';
    if (trade === 'other' && !tradeOther.trim()) e.trade_other = 'Tell us what you do.';
    if (!town.trim()) e.town = 'Which town or city are you in?';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goNext = () => {
    if (validateStep1()) setStep(2);
  };
  const goBack = () => setStep(1);

  const doSubmit = async () => {
    if (!validateStep1()) { setStep(1); return; }
    const intake = {
      trade,
      trade_other: trade === 'other' ? tradeOther.trim() : null,
      business_name: businessName.trim() || null,
      town: town.trim(),
      areas: areas.map((a) => a.trim()).filter(Boolean),
      services: services.map((s) => s.trim()).filter(Boolean),
      phone: phone.trim() || null,
      hours: hours.trim() || null,
      licence_number: showLicenceField ? (licenceNumber.trim() || null) : null,
      abn: abn.trim() || null,
      licensed_insured: licensedInsured === 'yes' ? true : licensedInsured === 'no' ? false : null,
      emergency_callouts: !!emergencyCallouts,
    };
    if (onSubmit) await onSubmit(intake);
  };

  // ------------------------------ RENDER ------------------------------
  const fieldWrap = 'flex flex-col gap-1.5';
  const label = 'text-[12.5px] uppercase tracking-wider';
  const inputCls = 'w-full px-3 py-2.5 rounded-lg border text-[15px] outline-none bg-white';
  const inputStyle = { borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' };
  const errStyle = { color: '#B4530A' };

  return (
    <div
      data-testid="guided-intake"
      className="bg-white rounded-2xl border p-5 sm:p-6 shadow-[0_10px_40px_-15px_rgba(23,21,59,0.2)] text-left"
      style={{ borderColor: 'var(--yb-border)' }}
    >
      {/* Step indicator */}
      <div className="flex items-center justify-between mb-5">
        <div className="inline-flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--yb-muted)' }}>
          <Hammer size={14} style={{ color: 'var(--yb-amber)' }} />
          <span>Guided setup — step {step} of 2</span>
        </div>
        {onAdvanced && (
          <button
            type="button"
            onClick={onAdvanced}
            data-testid="intake-advanced-toggle"
            className="text-[12.5px] underline decoration-dotted underline-offset-4"
            style={{ color: 'var(--yb-indigo)' }}
          >
            Advanced: write your own prompt
          </button>
        )}
      </div>

      {step === 1 && (
        <div className="space-y-4">
          {/* Trade */}
          <div className={fieldWrap}>
            <label className={label} style={{ color: 'var(--yb-muted)' }} htmlFor="intake-trade">
              Your trade <span style={{ color: '#B4530A' }}>*</span>
            </label>
            <select
              id="intake-trade"
              data-testid="intake-trade"
              value={trade}
              onChange={(e) => { setTrade(e.target.value); setServicesTouched(false); setServices([]); }}
              className={inputCls}
              style={inputStyle}
            >
              <option value="">Pick your trade…</option>
              {Object.entries(presets).map(([slug, p]) => (
                <option key={slug} value={slug}>{p.label}</option>
              ))}
            </select>
            {errors.trade && <p className="text-[13px]" style={errStyle}>{errors.trade}</p>}
            {trade === 'other' && (
              <input
                data-testid="intake-trade-other"
                value={tradeOther}
                onChange={(e) => setTradeOther(e.target.value)}
                placeholder="e.g. Roofer, painter, tiler"
                className={inputCls}
                style={inputStyle}
              />
            )}
            {trade === 'other' && errors.trade_other && (
              <p className="text-[13px]" style={errStyle}>{errors.trade_other}</p>
            )}
          </div>

          {/* Business name */}
          <div className={fieldWrap}>
            <label className={label} style={{ color: 'var(--yb-muted)' }} htmlFor="intake-business-name">
              Business name <span className="text-[11px] normal-case tracking-normal" style={{ color: 'var(--yb-muted)' }}>(optional)</span>
            </label>
            <input
              id="intake-business-name"
              data-testid="intake-business-name"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="e.g. Redcliffe Sparky Co."
              className={inputCls}
              style={inputStyle}
            />
          </div>

          {/* Town */}
          <div className={fieldWrap}>
            <label className={label} style={{ color: 'var(--yb-muted)' }} htmlFor="intake-town">
              Town or city <span style={{ color: '#B4530A' }}>*</span>
            </label>
            <input
              id="intake-town"
              data-testid="intake-town"
              value={town}
              onChange={(e) => setTown(e.target.value)}
              placeholder="e.g. Brisbane"
              className={inputCls}
              style={inputStyle}
            />
            {errors.town && <p className="text-[13px]" style={errStyle}>{errors.town}</p>}
          </div>

          {/* Areas covered — chips */}
          <div className={fieldWrap}>
            <label className={label} style={{ color: 'var(--yb-muted)' }} htmlFor="intake-area-input">
              Areas you cover <span className="text-[11px] normal-case tracking-normal" style={{ color: 'var(--yb-muted)' }}>(press Enter to add each suburb)</span>
            </label>
            <div className="flex flex-wrap gap-1.5 items-center rounded-lg border p-2" style={{ borderColor: 'var(--yb-border)', backgroundColor: 'white' }}>
              {areas.map((a) => (
                <span
                  key={a}
                  data-testid={`intake-area-chip-${a}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12.5px]"
                  style={{ backgroundColor: 'rgba(79,70,229,0.10)', color: 'var(--yb-indigo)' }}
                >
                  {a}
                  <button
                    type="button"
                    onClick={() => removeArea(a)}
                    aria-label={`Remove ${a}`}
                    className="hover:opacity-80"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
              <input
                id="intake-area-input"
                data-testid="intake-area-input"
                value={areaDraft}
                onChange={(e) => setAreaDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addArea(); }
                  else if (e.key === 'Backspace' && !areaDraft && areas.length) {
                    setAreas((prev) => prev.slice(0, -1));
                  }
                }}
                placeholder={areas.length ? '' : 'e.g. Redcliffe'}
                className="flex-1 min-w-[120px] text-[15px] outline-none bg-transparent px-1 py-1"
                style={{ color: 'var(--yb-ink)' }}
              />
            </div>
          </div>

          {/* Services tick-list */}
          {trade && (
            <div className={fieldWrap}>
              <div className="flex items-center justify-between">
                <label className={label} style={{ color: 'var(--yb-muted)' }}>
                  Services you offer
                </label>
                <div className="flex items-center gap-2 text-[12px]">
                  <button
                    type="button"
                    onClick={selectAllServices}
                    data-testid="intake-services-select-all"
                    className="underline decoration-dotted underline-offset-4"
                    style={{ color: 'var(--yb-indigo)' }}
                  >
                    Select all
                  </button>
                  {services.length > 0 && (
                    <button
                      type="button"
                      onClick={clearAllServices}
                      data-testid="intake-services-clear"
                      className="underline decoration-dotted underline-offset-4"
                      style={{ color: 'var(--yb-muted)' }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="rounded-lg border p-2 space-y-1" style={{ borderColor: 'var(--yb-border)' }}>
                {availableServices.length === 0 && (
                  <p className="px-2 py-2 text-[13px]" style={{ color: 'var(--yb-muted)' }}>
                    Add the services you offer below.
                  </p>
                )}
                {availableServices.map((s) => {
                  const checked = services.includes(s);
                  return (
                    <label
                      key={s}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-[#F5F4F0]"
                    >
                      <input
                        type="checkbox"
                        data-testid={`intake-service-${s.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                        checked={checked}
                        onChange={() => toggleService(s)}
                        className="w-4 h-4 accent-[var(--yb-indigo)]"
                      />
                      <span className="text-[14px]" style={{ color: 'var(--yb-ink)' }}>{s}</span>
                    </label>
                  );
                })}
              </div>
              <div className="flex gap-2 mt-1">
                <input
                  data-testid="intake-service-draft"
                  value={serviceDraft}
                  onChange={(e) => setServiceDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomService(); } }}
                  placeholder="Add your own (e.g. Solar hot water)"
                  className={inputCls}
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={addCustomService}
                  data-testid="intake-service-add"
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border text-[13px] shrink-0"
                  style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-ink)' }}
                >
                  <Plus size={14} /> Add
                </button>
              </div>

              {/* Icon preview strip — mirrors the icon the generator will
                  draw next to each service on the finished site. */}
              {services.length > 0 && (
                <div
                  className="mt-3 rounded-lg border p-3"
                  style={{ borderColor: 'var(--yb-border)', backgroundColor: '#FAFAF7' }}
                  data-testid="intake-service-icon-preview"
                >
                  <p className="text-[11.5px] uppercase tracking-wider mb-2" style={{ color: 'var(--yb-muted)' }}>
                    Icon preview
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {services.map((s) => (
                      <span
                        key={s}
                        data-testid={`intake-icon-preview-${s.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                        className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full border bg-white"
                        style={{ borderColor: 'var(--yb-border)' }}
                      >
                        <span style={{ color: 'var(--yb-indigo)' }}>
                          {serviceIconSvg(s, 16)}
                        </span>
                        <span className="text-[12.5px]" style={{ color: 'var(--yb-ink)' }}>{s}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Phone */}
          <div className={fieldWrap}>
            <label className={label} style={{ color: 'var(--yb-muted)' }} htmlFor="intake-phone">
              Phone <span className="text-[11px] normal-case tracking-normal" style={{ color: 'var(--yb-muted)' }}>(optional — becomes a placeholder if skipped)</span>
            </label>
            <input
              id="intake-phone"
              data-testid="intake-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 0400 000 000"
              inputMode="tel"
              className={inputCls}
              style={inputStyle}
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <p className="text-[12.5px]" style={{ color: 'var(--yb-muted)' }}>
              Anything you skip becomes an easy-to-fill placeholder on the page.
            </p>
            <button
              type="button"
              onClick={goNext}
              data-testid="intake-next"
              className="yb-btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13.5px] font-semibold"
            >
              Next <ArrowRight size={15} />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <p className="text-[13.5px]" style={{ color: 'var(--yb-muted)' }}>
            Optional extras — skip anything you don’t have handy. You can fill them later from the project page.
          </p>

          {/* Owner-photo nudge — a real photo of your van, shopfront or a finished job
              always beats any default stock image, no matter how well-curated. */}
          <div
            className="rounded-xl border p-4 flex items-start gap-3"
            style={{
              borderColor: 'var(--yb-amber)',
              backgroundColor: '#FFFBEB',
            }}
            data-testid="intake-photo-nudge"
          >
            <div className="shrink-0 mt-0.5" style={{ color: 'var(--yb-amber)' }}>
              <ImagePlus size={20} />
            </div>
            <div className="flex-1">
              <p className="text-[14px] font-semibold" style={{ color: '#92400E' }}>
                A photo of YOUR van, shopfront, or a finished job wins every time
              </p>
              <p className="text-[12.5px] mt-1" style={{ color: '#B4530A' }}>
                We use a curated stock image for now — but real photos convert far better.
                Upload yours from the project page as soon as your site is built (van,
                shopfront, before/after job shots all work great).
              </p>
            </div>
          </div>

          {/* Hours */}
          <div className={fieldWrap}>
            <label className={label} style={{ color: 'var(--yb-muted)' }} htmlFor="intake-hours">
              Business hours
            </label>
            <input
              id="intake-hours"
              data-testid="intake-hours"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder={DEFAULT_HOURS}
              className={inputCls}
              style={inputStyle}
            />
          </div>

          {/* Licence — only for licensed trades. Trade-specific label. */}
          {showLicenceField && (
            <div className={fieldWrap}>
              <label className={label} style={{ color: 'var(--yb-muted)' }} htmlFor="intake-licence">
                {licenceFieldLabel} <span className="text-[11px] normal-case tracking-normal" style={{ color: 'var(--yb-muted)' }}>(optional — kept private if left blank)</span>
              </label>
              <input
                id="intake-licence"
                data-testid="intake-licence"
                value={licenceNumber}
                onChange={(e) => setLicenceNumber(e.target.value)}
                placeholder="e.g. QLD 12345"
                className={inputCls}
                style={inputStyle}
              />
            </div>
          )}

          {/* ABN */}
          <div className={fieldWrap}>
            <label className={label} style={{ color: 'var(--yb-muted)' }} htmlFor="intake-abn">
              ABN
            </label>
            <input
              id="intake-abn"
              data-testid="intake-abn"
              value={abn}
              onChange={(e) => setAbn(e.target.value)}
              placeholder="e.g. 12 345 678 901"
              inputMode="numeric"
              className={inputCls}
              style={inputStyle}
            />
          </div>

          {/* Licensed & insured Y/N */}
          <div className={fieldWrap}>
            <label className={label} style={{ color: 'var(--yb-muted)' }}>
              Licensed &amp; insured? <span className="text-[11px] normal-case tracking-normal" style={{ color: 'var(--yb-muted)' }}>(only tick if you actually are — we never assume)</span>
            </label>
            <div className="flex gap-2">
              {['yes', 'no'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  data-testid={`intake-insured-${opt}`}
                  onClick={() => setLicensedInsured((cur) => (cur === opt ? '' : opt))}
                  className="px-4 py-2 rounded-full border text-[13.5px] capitalize"
                  style={{
                    borderColor: licensedInsured === opt ? 'var(--yb-indigo)' : 'var(--yb-border)',
                    color: licensedInsured === opt ? 'var(--yb-indigo)' : 'var(--yb-text)',
                    backgroundColor: licensedInsured === opt ? 'rgba(79,70,229,0.08)' : 'white',
                  }}
                >
                  {opt === 'yes' ? (
                    <span className="inline-flex items-center gap-1.5"><Check size={13} /> Yes</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">No</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Emergency callouts — neutral operational toggle. Default OFF. */}
          <div className={fieldWrap}>
            <label
              htmlFor="intake-emergency"
              className="flex items-start gap-2.5 p-3 rounded-lg border cursor-pointer hover:bg-[#F5F4F0]"
              style={{ borderColor: 'var(--yb-border)', backgroundColor: 'white' }}
            >
              <input
                id="intake-emergency"
                data-testid="intake-emergency-callouts"
                type="checkbox"
                checked={emergencyCallouts}
                onChange={(e) => setEmergencyCallouts(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[var(--yb-indigo)]"
              />
              <span className="flex-1">
                <span className="inline-flex items-center gap-1.5 text-[14px] font-medium" style={{ color: 'var(--yb-ink)' }}>
                  <Clock size={13} style={{ color: 'var(--yb-amber)' }} />
                  Emergency callouts 24/7
                </span>
                <span className="block text-[12.5px] mt-0.5" style={{ color: 'var(--yb-muted)' }}>
                  Tick if you take after-hours emergency jobs. Adds a badge to the trust row.
                </span>
              </span>
            </label>
          </div>

          <p className="text-[12.5px]" style={{ color: 'var(--yb-muted)' }}>
            Reviews use their own dedicated tool on the project page — you'll add them there.
          </p>

          <div className="flex items-center justify-between pt-2 gap-3 flex-wrap">
            <button
              type="button"
              onClick={goBack}
              data-testid="intake-back"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border text-[13.5px]"
              style={{ borderColor: 'var(--yb-border)', color: 'var(--yb-text)' }}
            >
              <ArrowLeft size={14} /> Back
            </button>
            <button
              type="button"
              onClick={doSubmit}
              disabled={busy}
              data-testid="intake-submit"
              className="yb-btn-primary inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13.5px] font-semibold disabled:opacity-70"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {busy ? 'Saving…' : submitLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidedIntake;
